import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { TranslateModule } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { debounceTime, filter } from 'rxjs';

import { UserSettings } from '@models/core';
import { CipoControlBase } from '@shared/components/fields/cipo-control.base';
import { ThousandSeparatorDirective } from '@shared/components/fields/cipo-number/thousand-separator.directive';
import { CipoNumberConfig, CipoNumberControl } from '@shared/components/fields/common';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoFormModule } from '@shared/modules/forms.module';
import { CipoValidators, UtilsService } from '@shared/services';
import { UserStore } from '@signal-store/user.store';
import { CipoTextViewComponent } from '../cipo-text-view/cipo-text-view.component';

@Component({
  selector: 'cipo-number',
  imports: [CipoFormModule, TranslateModule, ThousandSeparatorDirective, CipoTextViewComponent],
  templateUrl: './cipo-number.component.html',
})
export class CipoNumberComponent extends CipoControlBase<number | number[], CipoNumberConfig> implements AfterViewInit {
  utilsService = inject(UtilsService);
  userStore = inject(UserStore);
  chipInput = viewChild<ElementRef<HTMLInputElement>>('chipInput');
  numberInput = viewChild<ElementRef<HTMLInputElement>>('numberInput');
  formatting = signal('1.0-0');

  userSettings: UserSettings;
  selectedChipIndex: number;
  chipsField = new FormControl<string[]>([]);
  numberControl = new FormControl<string>(null);

  errorMessage = signal<{ key: string; params?: any }>(null);
  range = computed(() => {
    const { min, max } = this.fieldData() ?? {};
    return {
      hasMin: !isNil(min),
      min,
      hasMax: !isNil(max),
      max,
    };
  });
  singleHasValue = linkedSignal(() => {
    // this verifies if the initial value is set
    // when value changes will be updated in the effect that listen to control value changes
    const value = this.control().value;
    return !isNil(value);
  });

  constructor() {
    super();
    this.userSettings = this.userStore.calculatedUserSettings();

    effect(() => {
      const min = this.deepFieldData.min();
      const max = this.deepFieldData.max();
      const multiple = untracked(() => this.fieldData().multiple);
      const control = multiple ? this.chipsField : this.numberControl;
      if (!isNil(min)) {
        this.validators.set('min', multiple ? CipoValidators.eachElementMin(min) : Validators.min(min));
      } else {
        this.validators.delete('min');
      }

      if (!isNil(max)) {
        this.validators.set('max', multiple ? CipoValidators.eachElementMax(max) : Validators.max(max));
      } else {
        this.validators.delete('max');
      }
      /* This will also take care of required, because it's already set in the validators map. Be aware of the concurrency */
      this.updateValidators(control);
    });

    effect(() => {
      const currency = this.deepFieldData.currency();
      if (!currency) {
        return;
      }

      /* do not listen for fieldData, to avoid infinite loop */
      const mappedFieldData = this.utilsService.updateCipoFieldConfigIfCurrencyIsSet(untracked(() => this.fieldData()));
      this.fieldData.set(mappedFieldData);
    });

    effect(() => {
      // when control changes, we need to update the internal controls value
      const formValue = this.formValue();
      untracked(() => {
        if (this.fieldData()?.multiple) {
          const values = (Array.isArray(formValue) ? formValue : [formValue]).filter(v => !isNil(v));
          this.chipsField.setValue(
            values.map(v => this.formatNumber(v)),
            { emitEvent: false },
          );
        } else {
          const newValue = this.getSingleValue(formValue);
          this.singleHasValue.set(!isNil(newValue));
          this.numberControl.setValue(this.formatNumber(newValue), { emitEvent: false });
        }
      });
    });

    effect(() => {
      // update internal control for the single value
      const control = this.control();
      untracked(() => this.onControlChange(control));
    });

    effect(() => {
      // mark internal controls as touched/untouched based on the original control touched state to trigger validation messages
      const touched = this.controlTouched();
      if (touched) {
        this.numberControl.markAsTouched({ emitEvent: false });
        this.chipsField.markAsTouched({ emitEvent: false });
      } else {
        this.numberControl.markAsUntouched({ emitEvent: false });
        this.chipsField.markAsUntouched({ emitEvent: false });
      }
    });
  }

  ngAfterViewInit(): void {
    const fieldData = this.fieldData() ?? {};

    // focus the input if the field is focused
    if (fieldData.focused && this.numberInput()) {
      this.numberInput().nativeElement.focus();
    }

    // listen for status changes for the internal controls to update error messages
    const control = this.getInternalControl();
    control.statusChanges.subscribe(status => {
      if (status === 'INVALID') {
        const errors = control.errors;
        const currentFieldData = this.control()?.fieldData || fieldData || {};
        const { min: minValue, max: maxValue } = currentFieldData;
        if (errors['required']) {
          this.errorMessage.set({ key: 'errors.required' });
          return { key: 'errors.required' };
        }
        if (errors['min']) {
          this.errorMessage.set({ key: 'errors.minValue', params: { minValue } });
          return { key: 'errors.minValue', params: { minValue } };
        }
        if (errors['max']) {
          this.errorMessage.set({ key: 'errors.maxValue', params: { maxValue } });
          return { key: 'errors.maxValue', params: { maxValue } };
        }
        if (errors['custom']) {
          this.errorMessage.set({ key: errors['custom'] });
          return { key: errors['custom'] };
        }
      }
      this.errorMessage.set(null);
      return null;
    });
  }

  add(event: MatChipInputEvent) {
    const { chipInput, value } = event;
    if (!value) {
      return;
    }
    const formattedVal = this.parseNumber(value);
    if (formattedVal === null || isNaN(formattedVal)) {
      return;
    }
    const val = (this.control().value || []) as number[];
    if (this.selectedChipIndex !== undefined) {
      val[this.selectedChipIndex] = formattedVal;
      this.selectedChipIndex = undefined;
    } else {
      val.push(formattedVal);
    }
    this.control().setValue(val);
    chipInput.clear();
  }

  remove(index: number) {
    const val = this.control().value as number[];
    val.splice(index, 1);
    this.control().setValue(val);
  }

  edit(index: number) {
    this.selectedChipIndex = index;
    this.chipInput().nativeElement.value = this.formatNumber(this.control().value[index]);
    const values = this.control().value as number[];
    this.control().setValue(values);
    this.chipInput().nativeElement.focus();
  }

  // MARK: Blur event for number input
  onFocus(event: FocusEvent) {
    this.valueOnFocus = (event.target as HTMLInputElement).value;
  }

  onBlur(event: FocusEvent) {
    const newValue = (event.target as HTMLInputElement).value;
    if (this.valueOnFocus !== newValue) {
      this.onblur.emit();
    }
  }

  protected formatNumber(value: number): string {
    if (isNil(value)) {
      return null;
    }
    return this.utilsService.formatLocaleNumber(
      value,
      this.fieldData().decimals,
      this.userSettings?.decimalSeparator,
      this.userSettings?.thousandSeparator,
      this.fieldData().showThousandSeparator,
    );
  }

  private parseNumber(value: string): number {
    if (isNil(value)) {
      return null;
    }
    return this.utilsService.revertLocaleNumber(
      value,
      this.userSettings?.decimalSeparator,
      this.userSettings?.thousandSeparator,
    );
  }

  private onControlChange(control: CipoNumberControl) {
    // Setup the internal control to sync with the original control
    // it is used to interface with the owl-date-time-picker that need to show values in the user time zone and return UTC ISO strings
    this.setupInternalControl(control);

    // Sync disabled state between the original control and the internal control
    control.registerOnDisabledChange(isDisabled => {
      if (isDisabled) {
        this.numberControl.disable({ emitEvent: false });
      } else {
        this.numberControl.enable({ emitEvent: false });
      }
    });

    /* propagate custom error to internalControl */
    control.statusChanges
      .pipe(
        filter(status => status === 'INVALID' && control.errors?.['custom']),
        debounceTime(DEBOUNCE_TIMES.short),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const internalControl = this.getInternalControl();
        const customMessage = control.errors?.['custom'];
        internalControl.setErrors({ ...internalControl.errors, custom: customMessage });
        // Keep touched/dirty in sync with source control so Material error state is displayed.
        control.markAsTouched();
        control.markAsDirty();
        internalControl.markAsTouched();
        internalControl.markAsDirty();
      });

    // Initialize the disabled state
    if (control.disabled) {
      this.numberControl.disable({ emitEvent: false });
    }

    // Re-evaluate the validity of the control
    control.updateValueAndValidity({ emitEvent: false });
  }

  private getSingleValue<T extends number | string>(value: T | T[]): T {
    return Array.isArray(value) ? value[0] : value;
  }

  private getInternalControl(): FormControl {
    return this.fieldData().multiple ? this.chipsField : this.numberControl;
  }

  private setupInternalControl(control: CipoNumberControl) {
    // Create a clone FormControl for internal use
    // the value of this control is in the user's local decimal and thousand separator format
    const singleValue = this.getSingleValue(control.value);
    const internalControl = new FormControl(this.formatNumber(singleValue));

    // Sync changes from the internal control to the original control
    internalControl.valueChanges.subscribe(value => {
      // getting the invariant value from the internal control value (string value)
      const newValue = this.parseNumber(value);
      // Avoid emitting if the value is the same
      if (newValue === this.getSingleValue(control.value)) {
        return;
      }
      // emit the new value to the original control
      control.setValue(newValue);
      control.markAsDirty();
      control.markAsTouched();
      this.onblur.emit();
    });

    this.updateValidators(internalControl);
    // Replace the original control with the internal control in the template
    this.numberControl = internalControl;
  }
}
